import { DatePipe, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IconComponent, TypographyComponent } from 'components';
import { ChartLegendOption } from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile/components/chart-legend.component';
import { ChartConfigService } from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile/services/chart-config.service';
import { GetSocialListeningMyBrandAnalyticsVolumeService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-volume.service';
import { AnalyticsVolumeResponse } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-analytics.model';
import {
	ChartWidget,
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	formatCompactNumber,
	hasPositiveDataPointTotal
} from '../../../../services/report-preview-utils';
import {
	REPORT_CHART_PRIMARY_COLOR_UPPER,
	prepareForDrawing
} from '../../../../services/report-preview-chart-configs';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

interface KpiCard {
	label: string;
	value: string;
	color: string;
}

@Component({
	standalone: true,
	selector: 'app-volume-of-mentions-reach-slide',
	templateUrl: './volume-of-mentions-reach-slide.component.html',
	styleUrls: ['./volume-of-mentions-reach-slide.component.scss'],
	imports: [
		NgIf,
		IconComponent,
		TypographyComponent,
		NgChartsModule,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	],
	providers: [DatePipe]
})
export class VolumeOfMentionsReachSlideComponent
	implements OnChanges, OnDestroy
{
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	titleColor = '#2872e1';
	dateLabel = '';
	summaryText = '';
	kpiCards: KpiCard[] = [];
	primaryKpi: KpiCard | null = null;
	chartData: ChartConfiguration<'line'>['data'] | null = null;
	chartOptions: ChartConfiguration<'line'>['options'] | null = null;
	// Follows the data: a period too short for a line is drawn as bars.
	chartType: 'line' | 'bar' = 'line';
	legendOptions: ChartLegendOption[] = [];
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'volume_of_mentions_reach',
		null,
		null
	);

	private readonly destroy$ = new Subject<void>();
	private cachedVolume: AnalyticsVolumeResponse | null = null;
	private cacheKey: string | null = null;

	get isEmpty(): boolean {
		return (
			!this.cachedVolume ||
			![
				this.cachedVolume.mentionsData,
				this.cachedVolume.reachData,
				this.cachedVolume.socialMediaInteractionsData
			].some(series => hasPositiveDataPointTotal(series))
		);
	}

	constructor(
		private readonly volumeService: GetSocialListeningMyBrandAnalyticsVolumeService,
		private readonly chartConfigService: ChartConfigService,
		private readonly datePipe: DatePipe
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedVolume && this.cacheKey === currentCacheKey) {
			this.applyVolume(this.cachedVolume);
			return;
		}

		this.volumeService
			// Scope the series to the report's selected period (from/to) —
			// without it the endpoint defaults to all-time.
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(volume => {
				this.cachedVolume = volume;
				this.cacheKey = currentCacheKey;
				this.applyVolume(volume);
			});
	}

	private applyVolume(volume: AnalyticsVolumeResponse): void {
		const rawSlide = this.buildSlide(volume);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(volume: AnalyticsVolumeResponse): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;

		const seriesDefinitions = [
			{
				label: 'Mentions',
				color: REPORT_CHART_PRIMARY_COLOR_UPPER,
				data: volume.mentionsData
			},
			{ label: 'Reach', color: '#4FD1C5', data: volume.reachData },
			{
				label: 'Social Media Interactions',
				color: '#7BB3F5',
				data: volume.socialMediaInteractionsData
			}
		];

		const labels = seriesDefinitions[0].data.map(
			point => this.datePipe.transform(point.timestamp, 'MMM d') ?? ''
		);

		const datasets = seriesDefinitions.map((series, index) => {
			const isPrimary = index === 0;
			return {
				label: series.label,
				data: series.data.map(point => point.value),
				borderColor: series.color,
				// The soft fade beneath the primary line is left to the chart
				// widget, which builds it from this dataset's own colour: a
				// gradient written here is a function, and a function does not
				// survive the clone that applying any slide edit does.
				backgroundColor: isPrimary ? undefined : series.color,
				pointBackgroundColor: series.color,
				pointBorderColor: '#ffffff' as const,
				fill: isPrimary,
				borderWidth: 2,
				pointRadius: 0,
				pointHoverRadius: 3,
				tension: 0.4,
				type: 'line' as const
			};
		});

		const chartJsConfig: ChartConfiguration<'line'> = {
			type: 'line',
			data: { labels, datasets },
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: { display: false },
					tooltip: this.chartConfigService.getTooltipConfig() as any
				},
				scales: {
					x: {
						offset: false,
						border: { display: false },
						grid: { display: false },
						ticks: {
							color: '#6b7796',
							font: { family: 'Gilroy', size: 11 },
							maxRotation: 0,
							autoSkip: true,
							autoSkipPadding: 16
						}
					},
					y: {
						beginAtZero: true,
						border: { display: false },
						grid: { color: '#e6eaf2', drawTicks: false },
						ticks: {
							color: '#6b7796',
							font: { family: 'Gilroy', size: 11 },
							padding: 8
						}
					}
				} as any
			}
		};

		const sumValues = (data: { value: number }[]) =>
			data.reduce((accumulator, point) => accumulator + point.value, 0);
		const totals = {
			mentions: sumValues(volume.mentionsData),
			reach: sumValues(volume.reachData),
			engagements: sumValues(volume.socialMediaInteractionsData)
		};

		const chartWidget: ChartWidget = {
			id: `${prefix}_chart`,
			kind: 'chart',
			visible: true,
			content: {
				chartType: 'line',
				dataSourceKey: 'volume',
				seriesMeta: seriesDefinitions.map(series => ({
					key: series.label.toLowerCase().replace(/ /g, '_'),
					label: series.label,
					color: series.color
				})),
				chartJsConfig: chartJsConfig as unknown as Record<
					string,
					unknown
				>
			}
		};

		return {
			id: `${prefix}_slide`,
			slideType: 'volume_of_mentions_reach',
			title: this.item.title || 'Volume of Mentions & Reach',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Volume of Mentions & Reach',
						color: '#2872e1'
					}
				} as TitleWidget,
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				} as TitleWidget,
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: {
						text:
							this.item.description ||
							'Track mentions and reach across all channels over time.'
					}
				} as SummaryTextWidget,
				chartWidget,
				{
					id: `${prefix}_kpi_mentions`,
					kind: 'kpi',
					visible: true,
					content: {
						label: 'Total Mentions',
						value: formatCompactNumber(totals.mentions),
						amount: totals.mentions,
						legendColor: REPORT_CHART_PRIMARY_COLOR_UPPER
					}
				},
				{
					id: `${prefix}_kpi_reach`,
					kind: 'kpi',
					visible: true,
					content: {
						label: 'Total Reach',
						value: formatCompactNumber(totals.reach),
						amount: totals.reach,
						legendColor: '#4FD1C5'
					}
				},
				{
					id: `${prefix}_kpi_engagements`,
					kind: 'kpi',
					visible: true,
					content: {
						label: 'Social Media Interactions',
						value: formatCompactNumber(totals.engagements),
						amount: totals.engagements,
						legendColor: '#7BB3F5'
					}
				}
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			widget => widget.kind === 'title' && !hidden.has(widget.id)
		) as TitleWidget | undefined;

		const dateWidget = slide.widgets.find(
			widget => widget.kind === 'date_range' && !hidden.has(widget.id)
		) as TitleWidget | undefined;

		const summaryWidget = slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';

		const chartWidget = (slide.widgets.find(
			widget => widget.kind === 'chart' && !hidden.has(widget.id)
		) ?? null) as ChartWidget | null;

		if (chartWidget) {
			const config = chartWidget.content
				.chartJsConfig as unknown as ChartConfiguration<'line'>;
			// The design shows a single accent Mentions area line, so only
			// the primary series is rendered; the emitted widget keeps every
			// series intact for downstream consumers.
			this.chartData = config?.data
				? {
						labels: config.data.labels,
						datasets: config.data.datasets.slice(0, 1)
				  }
				: null;
			// The fade under the line is built here, from the line's own
			// colour, rather than written into the widget: this slide draws
			// straight onto a canvas, and a gradient carried on the widget
			// would not survive the clone that applying a slide edit does.

			this.chartOptions = config?.options ?? null;
			if (this.chartData) {
				// Drawn from what this slide shows — the primary series alone —
				// so the decision to fall back to bars is taken on that series.
				const drawn = {
					type: 'line',
					data: this.chartData,
					options: this.chartOptions ?? {}
				} as unknown as ChartConfiguration;
				this.chartType = prepareForDrawing(drawn) as 'line' | 'bar';
				this.chartOptions = (drawn as any).options;
			}
			this.legendOptions = (chartWidget.content.seriesMeta ?? []).map(
				seriesMeta => ({
					title: seriesMeta.label,
					color: seriesMeta.color
				})
			);
		} else {
			this.chartData = null;
			this.chartOptions = null;
			this.legendOptions = [];
		}

		// Redrawn as another type, the chart shows what this one shows: the
		// Mentions series alone, in the colour it is drawn in here.
		const mentions = this.chartData?.datasets[0];
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			mentions
				? sectionChartSource(this.item.id, {
						labels: (this.chartData?.labels ?? []) as string[],
						series: [
							{
								key: 'mentions',
								label: 'Mentions',
								values: mentions.data as number[],
								color: mentions.borderColor as string
							}
						]
				  })
				: null
		);

		this.kpiCards = (
			slide.widgets.filter(
				widget => widget.kind === 'kpi' && !hidden.has(widget.id)
			) as any[]
		).map(card => ({
			label: card.content.label,
			value: card.content.value,
			color: card.content.legendColor ?? '#2872E1'
		}));

		// The callout shows only the primary (Mentions) total, matching the
		// single-series design.
		this.primaryKpi = this.kpiCards[0] ?? null;
	}
}
